import * as React from "react";
import { useNavigate } from "@opendash/router";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  ChartSection,
  Card,
  InfoBox,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";

export const WasserverbrauchRoute = React.memo(function WasserverbrauchRoute() {
  const navigate = useNavigate();
  const { t } = useTexts();

  const tabs = [
    {
      title: t("b.tab.1.title", "Die Bedeutung von Wasser"),
      path: "/wasserklaerer/wasserangebot/angebot",
      isActive: false,
    },
    {
      title: t("b.tab.2.title", "Wo kommt das Wasser her?"),
      path: "/wasserklaerer/wasserangebot/herkunft",
      isActive: false,
    },
    {
      title: t("b.tab.3.title", "Wasserverbrauch in Deutschland"),
      path: "/wasserklaerer/wasserangebot/verbrauch",
      isActive: true,
    },
    {
      title: t("b.tab.4.title", "Entwicklung des Wasserverbrauchs"),
      path: "/wasserklaerer/wasserangebot/entwicklung",
      isActive: false,
    },
  ];

  const mainChartOptions = {
    chart: {
      type: "pie",
      height: 500,
    },
    title: {
      text: t(
        "wasserverbrauch.chart.main.title",
        "Wasserverbrauch in Deutschland nach Sektoren"
      ),
      style: {
        fontSize: "20px",
        fontWeight: "bold",
        color: "#2c3e50",
      },
    },
    subtitle: {
      text: t(
        "wasserverbrauch.chart.main.subtitle",
        "Gesamtverbrauch des Menschen: 17,9 Mrd. m³ Wasser"
      ),
      style: {
        fontSize: "16px",
        color: "#666",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.percentage:.1f}%",
        },
        showInLegend: false,
        innerSize: "40%", // Donut-Diagramm
        colors: ["#00897b", "#26a69a", "#1e88e5", "#66bb6a"],
      },
    },
    series: [
      {
        name: "Wasserverbrauch",
        data: [
          {
            name: "Energieversorgung",
            y: 38.6,
            color: "#00897b",
          },
          {
            name: "Bergbau und verarbeitendes Gewerbe",
            y: 29.1,
            color: "#26a69a",
          },
          {
            name: "Öffentliche Wasserversorgung",
            y: 29.8,
            color: "#1e88e5",
          },
          {
            name: "Landwirtschaft",
            y: 2.5,
            color: "#66bb6a",
          },
        ],
      },
    ],
    legend: {
      enabled: false,
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    },
    credits: {
      enabled: false,
    },
  };

  const publicWaterChartOptions = {
    chart: {
      type: "pie",
      height: 500,
    },
    title: {
      text: t(
        "wasserverbrauch.chart.public.title",
        "Aufteilung Öffentliche Wasserversorgung"
      ),
      style: {
        fontSize: "20px",
        fontWeight: "bold",
        color: "#2c3e50",
      },
    },
    subtitle: {
      text: t(
        "wasserverbrauch.chart.public.subtitle",
        "Gesamtverbrauch öffentliche Wasserversorgung: 5,3 Mrd. m³ Wasser"
      ),
      style: {
        fontSize: "16px",
        color: "#666",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.percentage:.1f}%",
        },
        showInLegend: false,
        innerSize: "40%", // Donut-Diagramm
        colors: ["#1565c0", "#1e88e5", "#00897b"],
      },
    },
    series: [
      {
        name: "Öffentliche Wasserversorgung",
        data: [
          {
            name: "Private Haushalte und Kleingewerbe",
            y: 71.7,
            color: "#1565c0",
          },
          {
            name: "Schulen, Behörden, Krankenhäuser und gewerbliche Unternehmen",
            y: 16.3,
            color: "#1e88e5",
          },
          {
            name: "Wasserverluste und Eigenverbrauch der Wasserwerke",
            y: 12.0,
            color: "#00897b",
          },
        ],
      },
    ],
    legend: {
      enabled: false,
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    },
    credits: {
      enabled: false,
    },
  };

  return (
    <AppLayout>
      <ContentContainer>
        <h1>{t("wasserverbrauch.title", "Wasserverbrauch in Deutschland")}</h1>

        <TabContainer>
          {tabs.map((tab, index) => (
            <TabButton
              key={index}
              isActive={tab.isActive}
              onClick={() => !tab.isActive && navigate(tab.path)}
            >
              {tab.title}
            </TabButton>
          ))}
        </TabContainer>

        <ChartSection>
          <HighchartsReact highcharts={Highcharts} options={mainChartOptions} />
          <FigureCaption>
            {processInlineFormatting(
              t(
                "wasserverbrauch.chart.main.caption",
                "Abbildung 1: Wasserverbrauch in Deutschland nach Sektoren in Prozent (2022) [1]"
              )
            )}
          </FigureCaption>
          <div style={{ textAlign: "left" }}>
            <br />
            <p>
              {t(
                "wasserverbrauch.intro.p1",
                "Diese Grafik veranschaulicht die wichtigsten Sektoren des Wasserverbrauchs in Deutschland und macht deutlich, in welchen Bereichen die größten Mengen an Wasser genutzt werden. Besonders hervorzuheben sind drei Sektoren, die zusammen den größten Anteil am Wasserverbrauch haben: die Energieversorgung, der Bergbau und das verarbeitende Gewerbe sowie die öffentliche Wasserversorgung."
              )}
            </p>

            <p>
              {t(
                "wasserverbrauch.intro.p2",
                "Die Energieversorgung benötigt enorme Wassermengen, insbesondere für Kühlprozesse in Kraftwerken. Im Bergbau und im verarbeitenden Gewerbe wird Wasser für verschiedene industrielle Prozesse verwendet, während die öffentliche Wasserversorgung Haushalte und Unternehmen mit Trinkwasser versorgt."
              )}
            </p>

            <p>
              {t(
                "wasserverbrauch.intro.p3",
                "Eine zentrale Frage ist, wie sich der Wasserverbrauch in diesen drei Bereichen über die Jahre hinweg verändert hat. Sind durch technologische Fortschritte und effizientere Nutzung bereits Einsparungen erzielt worden, oder ist der Verbrauch weiterhin hoch?"
              )}
            </p>
          </div>
        </ChartSection>

        <ChartSection>
          <HighchartsReact
            highcharts={Highcharts}
            options={publicWaterChartOptions}
          />
          <FigureCaption>
            {processInlineFormatting(
              t(
                "wasserverbrauch.chart.public.caption",
                "Abbildung 2: Aufteilung des Wasserverbrauchs innerhalb des Sektors der öffentlichen Wasserversorgung (in Prozent) [2]"
              )
            )}
          </FigureCaption>
        </ChartSection>

        <Card>
          <h2>
            {t(
              "wasserverbrauch.kleingewerbe.title",
              "Warum wird der Kleingewerbeanteil beim häuslichen Wasserverbrauch mitgerechnet?"
            )}
          </h2>

          <p>
            {processInlineFormatting(
              t(
                "wasserverbrauch.kleingewerbe.p1",
                "In vielen Mehrfamilienhäusern befinden sich kleine Gewerbebetriebe wie Friseure, Bäckereien oder Arztpraxen, die über denselben Wasseranschluss wie die privaten Haushalte versorgt werden. Ihr Wasserverbrauch fließt daher automatisch in die Statistik des Haushaltsverbrauchs ein. [2]"
              )
            )}
          </p>
        </Card>

        {/* Literaturverzeichnis */}
        <SourcesSection>
          <h3>
            {t("wasserverbrauch.literatur.title", "Literaturverzeichnis")}
          </h3>
          <ol>
            <li id="quelle-1">
              <LinkifyText>
                {t(
                  "wasserverbrauch.literatur.ref1",
                  `[1] Umweltbundesamt (Hrsg.), \u201EWasserressourcen und ihre Nutzung\u201C, Umweltbundesamt, 2025. Zugegriffen: 1. Dezember 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/daten/wasser/wasserressourcen-ihre-nutzung`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-2">
              <LinkifyText>
                {t(
                  "wasserverbrauch.literatur.ref2",
                  `[2] Umweltbundesamt (Hrsg.), \u201EÖffentliche Wasserversorgung\u201C, Umweltbundesamt, 2024. Zugegriffen: 1. Dezember 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/daten/wasser/wasserwirtschaft/oeffentliche-wasserversorgung`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
          </ol>
        </SourcesSection>
      </ContentContainer>
    </AppLayout>
  );
});
